// Datasources tab — flat inventory grid + headline KPIs.
//
// Mirrors web/scripts/admin.js's loadDatasources() / DATASOURCES_COLUMN_DEFS.
// Smallest tab — no filters, no pagination, no row actions.

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import type { DatasourceRow, OrgScope } from "../../api/admin";
import { adminApi } from "../../api/admin";
import { ErrorBlock, KpiCard, LoadingBlock, formatNumber } from "./_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
}

export function DatasourcesTab({ bearer, scope }: Props) {
  const [rows, setRows] = useState<DatasourceRow[]>([]);
  const [kpis, setKpis] = useState<{
    total_datasources?: number;
    total_tables?: number;
    org_count?: number;
    active_datasources_30d?: number;
  } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([adminApi.datasources(bearer, scope), adminApi.overview(bearer, scope)])
      .then(([dsRows, overview]) => {
        if (cancelled) return;
        setRows(dsRows);
        setKpis(overview.datasources_kpis ?? {});
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope]);

  const colDefs = useMemo<ColDef<DatasourceRow>[]>(
    () => [
      { field: "organization", headerName: "Organization", width: 200 },
      { field: "datasource_name", headerName: "Datasource", flex: 1 },
      { field: "storage_type", headerName: "Storage Type", width: 160 },
      { field: "table_count", headerName: "Tables", width: 110, type: "numericColumn" },
    ],
    [],
  );

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label="Total Datasources" value={formatNumber(kpis?.total_datasources)} />
        <KpiCard label="Total Tables" value={formatNumber(kpis?.total_tables)} />
        <KpiCard label="Organizations" value={formatNumber(kpis?.org_count)} />
        <KpiCard
          label="Active Datasources (30d)"
          value={formatNumber(kpis?.active_datasources_30d)}
        />
      </div>

      {error && <ErrorBlock error={error} />}

      <div className="ag-theme-quartz" style={{ width: "100%", height: 480 }}>
        <AgGridReact
          rowData={rows}
          columnDefs={colDefs}
          defaultColDef={{ sortable: true, filter: true, resizable: true }}
          pagination={true}
          paginationPageSize={25}
        />
      </div>
    </div>
  );
}
